import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContractById, getPaymentsForContract, Contract, Payment, ContractStatus, updateContractStatus } from '../../services/contractService';
import useAuthStore from '../../store/authStore';
import { ArrowRightIcon, CalendarIcon, CurrencyDollarIcon, DocumentTextIcon, ChevronLeftIcon, CheckIcon, XIcon } from '@heroicons/react/outline';
import OTPVerification from '../../components/OTPVerification';


const ContractDetailsPage: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<Contract | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { userData } = useAuthStore();
  const isLawyer = userData?.role === 'lawyer';
  const isClient = userData?.role === 'client';
  const isPendingContract = contract?.status === 'معلق';
  const isRejectedContract = contract?.status === 'مرفوض';
  
  
  // Debug logs
  console.log('User role:', userData?.role);
  console.log('Contract status:', contract?.status);
  console.log('isClient:', isClient, 'isPendingContract:', isPendingContract);

  // Check for success message from OTP verification
  useEffect(() => {
    const state = window.history.state?.state;
    if (state?.success) {
      alert(state.message);
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({ ...window.history.state, state: null }, '');
    }
  }, []);

  useEffect(() => {
    if (!contractId) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const contractData = await getContractById(contractId);
        const paymentsData = await getPaymentsForContract(contractId);
        setContract(contractData);
        setPayments(paymentsData);
      } catch (error) {
        console.error('Failed to fetch contract details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contractId]);

  if (isLoading) {
    return <div className="p-5 text-center">جاري التحميل...</div>;
  }

  if (!contract) {
    return <div className="p-5 text-center">لم يتم العثور على العقد.</div>;
  }

  const handleStatusUpdate = async (status: ContractStatus) => {
    if (!contractId) return false;
    
    try {
      await updateContractStatus(contractId, status);
      setContract({ ...contract, status });
      return true;
    } catch (error) {
      console.error('Error updating contract status:', error);
      alert('حدث خطأ أثناء تحديث حالة العقد');
      return false;
    }
  };

  const handleApprove = () => {
    if (!contractId) return;
    navigate(`/contracts/${contractId}/acknowledge?action=approve&title=${encodeURIComponent(contract?.title || '')}&lawyerName=${encodeURIComponent(contract?.lawyerName || '')}`);
  };

  const handleReject = () => {
    navigate(`/contracts/${contractId}/verify-otp?action=reject`);
  };



  const getStatusChipClass = (status: Payment['status']) => {
    switch (status) {
      case 'مدفوع': return 'bg-green-100 text-green-800';
      case 'مستحق': return 'bg-amber-100 text-amber-800';
      case 'متأخر': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-50 text-gray-800';
    }
  };

  return (
    <>


      <div className="p-4 bg-gray-50 min-h-screen font-tajawal mb-24">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate(-1)}
          style={{
            padding: '0.5rem',
            borderRadius: '9999px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
            outline: 'none'
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = '#e5e7eb'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <ChevronLeftIcon style={{ height: '1.25rem', width: '1.25rem', color: '#374151', transform: 'rotate(180deg)' }} />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mr-4">تفاصيل العقد</h2>
      </div>

      {/* Contract Actions - For Clients with Pending Contracts */}
      {isClient && isPendingContract && (
        <div className="bg-white p-5 rounded-3xl shadow-md mb-6">
          <h3 className="font-bold text-lg mb-4">إجراءات العقد</h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={handleReject}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                borderRadius: '0.75rem',
                fontWeight: 500,
                border: '1px solid #fecaca',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                outline: 'none',
                fontFamily: 'inherit',
                width: '100%'
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#fee2e2'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(220, 38, 38, 0.5)'}
              onBlur={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <XIcon style={{ height: '1.25rem', width: '1.25rem' }} />
              رفض العقد
            </button>
            <button
              onClick={handleApprove}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: 'oklch(0.55 0.24 262.91)',
                color: '#ffffff',
                borderRadius: '0.75rem',
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                outline: 'none',
                fontFamily: 'inherit',
                width: '100%'
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
              onBlur={e => e.currentTarget.style.boxShadow = 'none'}
              onMouseDown={e => e.currentTarget.style.backgroundColor = 'oklch(0.45 0.24 262.91)'}
              onMouseUp={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
            >
              <CheckIcon style={{ height: '1.25rem', width: '1.25rem' }} />
              الموافقة على العقد
            </button>
          </div>
        </div>
      )}

      {/* Contract Info Card */}
      <div className="bg-white p-6 rounded-3xl shadow-md mb-6">
        <div className="flex items-start mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mr-4">
                <h2 className="font-bold text-lg text-gray-900">{contract.title}</h2>
                <p className="text-sm text-gray-500">معرف العقد: {contract.id}</p>
            </div>
        </div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">{contract.description}</p>
        <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center text-gray-700">
                <CurrencyDollarIcon className="h-5 w-5 ml-2 text-gray-400" />
                <span>المبلغ الإجمالي: <strong>{contract.amount.toLocaleString('en-US')} {contract.currency}</strong></span>
            </div>
            <div className="flex items-center text-gray-700">
                <CalendarIcon className="h-5 w-5 ml-2 text-gray-400" />
                <span>تاريخ الإنشاء: <strong>{new Date(contract.createdAt).toLocaleDateString('en-US')}</strong></span>
            </div>
            <div className="flex items-center text-gray-700">
                 <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${contract.status === 'نشط' ? 'bg-blue-100 text-blue-800' : 'bg-gray-50 text-gray-800'}`}>
                    {contract.status}
                </span>
            </div>
            <div className="flex items-center text-gray-700">
                <CalendarIcon className="h-5 w-5 ml-2 text-gray-400" />
                <span>تاريخ الاستحقاق: <strong>{new Date(contract.dueDate).toLocaleDateString('en-US')}</strong></span>
            </div>
        </div>
      </div>

      {/* Payment Schedule for Client Approval */}
      {isClient && isPendingContract && (
        <div className="bg-white p-5 rounded-3xl shadow-md mb-6">
          <h3 className="font-bold text-lg mb-4 text-right">جدول الدفعات</h3>
          <p className="text-gray-600 text-sm mb-4 text-right">يرجى مراجعة تفاصيل الدفعات قبل الموافقة على العقد</p>
          
          <div className="space-y-3 mb-4">
            {payments.map((payment, index) => (
              <div 
                key={payment.id || index}
                className="bg-gray-50 rounded-2xl p-4 border border-gray-100"
              >
                <div className="flex justify-between items-center">
                  <div className="text-right">
                    <p className="text-gray-500 text-sm">الدفعة {index + 1}</p>
                    <p className="text-gray-700 font-medium">
                      {new Date(payment.dueDate).toLocaleDateString('en-SA')}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl px-3 py-2 border border-gray-200">
                    <p className="text-gray-900 font-bold">
                      {payment.amount.toLocaleString('en-SA')} {contract.currency}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        
        </div>
      )}

      {/* PDF Download CTA */}
      <div className="bg-white p-5 rounded-3xl shadow-md mb-6">
          <div className="text-center">
            <DocumentTextIcon className="h-12 w-12 text-blue-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">تحميل نسخة من العقد</h3>
            <p className="text-gray-600 mb-4">يمكنك تحميل نسخة PDF من العقد لمشاركتها مع العميل أو حفظها للسجلات</p>
            <button 
              onClick={() => console.log('Downloading contract PDF:', contractId)}
              style={{
                width: '100%',
                maxWidth: '20rem',
                margin: '0 auto',
                padding: '0.75rem 1.5rem',
                backgroundColor: 'oklch(0.55 0.24 262.91)',
                color: '#ffffff',
                fontWeight: 500,
                borderRadius: '0.75rem',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'background-color 0.2s',
                outline: 'none'
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
              onBlur={e => e.currentTarget.style.boxShadow = 'none'}
              onMouseDown={e => e.currentTarget.style.backgroundColor = 'oklch(0.45 0.24 262.91)'}
              onMouseUp={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" style={{ height: '1.25rem', width: '1.25rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              تحميل العقد PDF
            </button>
          </div>
        </div>
      

      {/* Payments List - Only for Clients */}
      {!isLawyer && !isPendingContract && !isRejectedContract &&(
        <div className="bg-white p-5 rounded-3xl shadow-md">
          <h3 className="font-bold text-lg mb-4">الدفعات</h3>
          
          {payments.length === 0 ? (
            <p className="text-center text-gray-500 py-4">لا توجد دفعات مجدولة لهذا العقد.</p>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => (
                <div 
                  key={payment.id} 
                  className={`p-4 rounded-xl border ${payment.status === 'مستحق' ? 'border-amber-200 bg-amber-50' : 'border-gray-50'} ${payment.status === 'مستحق' ? 'active:bg-amber-100' : ''} transition-colors`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-gray-900">
                          {payment.amount.toLocaleString('en-US')} {contract.currency}
                        </span>
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusChipClass(payment.status)}`}>
                          {payment.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <CalendarIcon className="h-4 w-4 ml-1 text-gray-400" />
                        <span>الاستحقاق: {new Date(payment.dueDate).toLocaleDateString('en-US')}</span>
                      </div>
                      
                      {payment.paidDate && (
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <CalendarIcon className="h-4 w-4 ml-1 text-gray-400" />
                          <span>تاريخ الدفع: {new Date(payment.paidDate).toLocaleDateString('en-US')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {payment.status === 'مستحق' && (
                    <div className="mt-3 pt-3 border-t border-gray-50">
                      <button 
                        onClick={() => navigate(`/payments/${contractId}/${payment.id}`)}
                        style={{
                          width: '100%',
                          padding: '0.625rem',
                          backgroundColor: 'oklch(0.55 0.24 262.91)',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          borderRadius: '0.5rem',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          outline: 'none',
                          fontFamily: 'inherit',
                          lineHeight: '1.25'
                        }}
                        onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
                        onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
                        onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
                        onBlur={e => e.currentTarget.style.boxShadow = 'none'}
                        onMouseDown={e => e.currentTarget.style.backgroundColor = 'oklch(0.45 0.24 262.91)'}
                        onMouseUp={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
                      >
                        سدد الآن
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
    </>
  );
};

export default ContractDetailsPage;
